// Override rules, grouped by the thing that decides which one wins.
//
// The flat list this replaces showed nine identical forms in an order nobody
// chose. Order is not the user's here and never was: `setOverrides` splits the
// rules by their `should` and sorts each group by segment strength, so what is
// typed is discarded and the sorted list comes back. That precedence was
// invisible, which made the impact note's "a higher priority rule of the same
// kind reached them first" refer to something not on screen.
//
// So the groups ARE the precedence, in the order the engine runs them, headed
// by the phrase the builder itself offers rather than a friendlier invention.
//
// A collapsed row reads as a sentence and opens into the same `RuleComponent`
// the unflagged Overrides page uses. Nothing here rewrites the editing surface:
// this is a container and a reading, so `RuleEditor` is untouched and the two
// pages that mount it do not move.
import cloneDeep from 'lodash/cloneDeep';
import { toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { Button } from 'react-bootstrap';

import { IRulePageModel, Rule, Should } from '../../../models/rules';
import { ModelType } from '../../../models/ModelItem';
import { Subject } from '../../../models/sqlform';
import { PROFILES_PERMISSIONS } from '../../../permissions';
import Store from '../../../store';
import checkPermissions from '../CheckPermissions';
import RuleComponent from '../rules/RuleComponent';
import { RuleImpact } from './overrideImpactApi';
import RuleImpactNote from './RuleImpactNote';
import {
  segmentFill,
  segmentInk,
  segmentLabel,
  vocabularyOf,
} from './segmentVocabulary';
import { overrideRuleSentence } from './overrideSentence';
import './overrideRules.css';

/** The three kinds, in the order `setOverrides` runs them. */
const GROUPS: Should[] = [
  'should be',
  'should at most be',
  'should at least be',
];

const ORDINAL = ['runs 1st', 'runs 2nd', 'runs 3rd'];

/** The rule the "Create new rule" button makes, matching what the form expects. */
function defaultRule(modelType: ModelType): Rule {
  return {
    ruleType: 'form',
    formConditions: [
      {
        subject:
          modelType === 'customer_fit'
            ? {
                subject: 'domain',
                type: 'string',
                context: 'standard',
                tags: [],
              }
            : undefined,
        verb: 'is',
        complements: [''],
        lower: true,
      },
    ],
    rule: 'should be',
    segment: modelType === 'customer_fit' ? 'very good' : 'very high',
  } as Rule;
}

function OverrideRules({
  store,
  model,
  formFields,
  updateRules,
  saveRules,
  onDirtyChange,
  modelType,
  isAllowedToEdit,
  impacts,
  impactTotal,
  impactConversions,
}: {
  store?: Store;
  model: IRulePageModel;
  formFields: Subject[];
  updateRules: (rules: Rule[]) => void;
  /**
   * Write the rules, and say whether they reached the server.
   *
   * The page held the edit and committed it when focus left the card, which
   * is a gesture with nothing on screen describing it. This is that commit
   * made into a button. The answer decides whether the row closes: `false`
   * means the write was refused, and the card's own note says which rule is
   * holding it.
   */
  saveRules?: () => Promise<boolean>;
  /** A rule is open, edited and unsaved, so leaving the page would lose it. */
  onDirtyChange?: (dirty: boolean) => void;
  modelType: ModelType;
  isAllowedToEdit: boolean;
  /** What each rule does, keyed by its position in the stored list. */
  impacts?: RuleImpact[] | null;
  impactTotal?: number;
  impactConversions?: number;
}) {
  const { rules } = model;

  // Which rule is open for editing, by its position in the stored list. One at
  // a time: the point of collapsing them is that the set is readable at a
  // glance, and two open forms is most of the way back to where this started.
  //
  // It is also what makes a per-rule Save honest. `setOverrides` writes the
  // whole list, so a second half-typed rule left open somewhere would be
  // written by the first rule's Save.
  const [openRule, setOpenRule] = React.useState<number | null>(null);

  /** Leaving an edited rule was just refused. Cleared by Save or Cancel. */
  const [blocked, setBlocked] = React.useState(false);

  /** The open rule as it looked when it was opened, so an untouched rule can
      be told from an edited one and Save can stay disabled on the first. */
  const openedAs = React.useRef<string | null>(null);
  const openedFor = React.useRef<number | null>(null);

  const snapshot = (index: number | null) =>
    index === null ? null : JSON.stringify(toJS(rules[index]));

  // Taken while rendering rather than in an effect. An effect runs AFTER the
  // render that opened the rule, so the comparison below would run once
  // against the previous rule's snapshot and report a change nobody made.
  if (openedFor.current !== openRule) {
    openedFor.current = openRule;
    openedAs.current = snapshot(openRule);
  }

  const isDirty = openRule !== null && snapshot(openRule) !== openedAs.current;

  React.useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty]);

  /**
   * Open another rule, or close this one.
   *
   * Refuses while the open rule has unsaved changes, rather than discarding
   * them or writing them behind the person's back. Save and Cancel are the two
   * ways out and they sit at the bottom of the open form.
   */
  const requestOpen = (index: number | null) => {
    if (isDirty) {
      setBlocked(true);
      return;
    }
    setBlocked(false);
    setOpenRule(index);
  };

  /** Put the open rule back the way it was found, and close it. */
  const cancelEdit = () => {
    if (openRule !== null && openedAs.current !== null) {
      const next = rules.slice();
      next[openRule] = JSON.parse(openedAs.current);
      updateRules(next);
    }
    setBlocked(false);
    setOpenRule(null);
  };

  /**
   * Write the rule, and close it only if the write landed.
   *
   * A rule that is still missing a condition is refused by the page, which
   * names it above the list. Collapsing it then would hide the thing the note
   * is pointing at.
   */
  const saveEdit = async () => {
    if (!saveRules) return;
    const landed = await saveRules();
    if (!landed) return;
    setBlocked(false);
    setOpenRule(null);
  };

  // Positions are carried alongside the rules rather than recomputed, because
  // `impacts` and `RuleComponent` both address a rule by its index in the
  // stored list and grouping renumbers nothing.
  const placed = rules.map((rule, index) => ({ rule, index }));

  /**
   * What `RuleComponent` hands back, made into a change anything can see.
   *
   * It mutates the rules array IN PLACE and returns the same reference, so
   * passing the page's handler to it directly means a keystroke produces no new
   * identity, nothing re-renders, and the controlled input rejects the
   * character: the field reads as frozen. `RuleEditor` has always cloned here,
   * in `updateRulesArray`, and this is that same wrapper.
   */
  const commitRules = (next: Rule[]) => updateRules(cloneDeep(next));

  const createRule = () => {
    // The new rule would be the open one, so an edit in progress elsewhere
    // would be left behind unsaved with nothing pointing at it.
    if (isDirty) {
      setBlocked(true);
      return;
    }
    const next = rules;
    next.push(defaultRule(modelType));
    updateRules(next);
    // Opened straight away: a new rule is created to be filled in, and a
    // collapsed empty row would have to be found and clicked first.
    setBlocked(false);
    setOpenRule(next.length - 1);
  };

  const removeRule = (index: number) => {
    // Deleting writes the whole list, so an unsaved edit in a DIFFERENT rule
    // would be written along with it. Deleting the rule being edited is fine:
    // the edit goes with it.
    if (isDirty && openRule !== index) {
      setBlocked(true);
      return;
    }
    const next = rules.slice();
    next.splice(index, 1);
    updateRules(next);
    setBlocked(false);
    setOpenRule(null);
  };

  const groups = GROUPS.map((should, position) => ({
    should,
    ordinal: ORDINAL[position],
    members: placed.filter(({ rule }) => rule.rule === should),
  })).filter((group) => group.members.length);

  // A rule whose `should` is none of the three would otherwise be invisible on
  // a page that saves by itself, which is the worst way to lose one.
  const ungrouped = placed.filter(({ rule }) => !GROUPS.includes(rule.rule));

  const row = ({ rule, index }: { rule: Rule; index: number }) => {
    const isOpen = openRule === index;
    const sentence = overrideRuleSentence(rule);
    return (
      <div className={`ovr-rule ${isOpen ? 'ovr-rule--open' : ''}`} key={index}>
        <div className="ovr-row">
          <button
            type="button"
            className="ovr-summary"
            onClick={() => requestOpen(isOpen ? null : index)}
            aria-expanded={isOpen}
          >
            {/* The same glyph every other collapsible in the revamp uses:
                the signals row, both performance cards, the tree diagnostics
                and the tree page. A FontAwesome chevron here was the only one
                of its kind and read as a lighter, different control. */}
            <span className="ovr-caret" aria-hidden="true">
              {isOpen ? '▼' : '►'}
            </span>
            <span className="ovr-sentence">
              {sentence || <em>No conditions yet</em>}
            </span>
            <span className="ovr-arrow" aria-hidden>
              &rarr;
            </span>
            {/* Filled from the model's own palette, so the destination reads
                as the same band the tiering bars draw: the fit one for a fit
                model, the engagement one for an account model. A segment
                neither names keeps a neutral fill rather than an invented
                colour. */}
            <span
              className="ovr-segment"
              style={{
                background:
                  segmentFill(vocabularyOf(modelType), rule.segment) ??
                  undefined,
                color:
                  segmentInk(vocabularyOf(modelType), rule.segment) ??
                  undefined,
              }}
            >
              {segmentLabel(vocabularyOf(modelType), rule.segment)}
            </span>
          </button>
          {isOpen && isDirty && <span className="ovr-unsaved">Unsaved</span>}
          {checkPermissions(
            PROFILES_PERMISSIONS.ARCHITECT,
            <Button
              variant="outline-secondary"
              size="sm"
              className="ovr-delete"
              title="Delete this rule"
              aria-label="Delete this rule"
              disabled={!isAllowedToEdit || rule.permanent}
              onClick={() => removeRule(index)}
            >
              <i aria-hidden className="fas fa-times" />
            </Button>
          )}
        </div>

        {/* Only once measured. `impacts` is null while loading, after a failed
            read and on a point based model, and a missing entry in a measured
            list is what "no leads land on this rule" means: rendered for null
            as well, that sentence was a finding nobody had made. */}
        {impacts && (
          <RuleImpactNote
            impact={impacts.find((entry) => entry.ruleIndex === index)}
            segment={rule.segment}
            total={impactTotal}
            totalConversions={impactConversions}
          />
        )}

        {isOpen && (
          <div className="ovr-form">
            <RuleComponent
              compact
              index={index}
              rule={rule}
              rules={rules}
              updateRules={commitRules}
              formFields={formFields}
              withRuleAndSegment
              withSubConditions
              isAggregationsForm={false}
              isAllowedToEdit={isAllowedToEdit}
            />
            {saveRules && (
              <div className="ovr-commit">
                {blocked && (
                  <span className="ovr-commit-note">
                    Save or cancel this rule first.
                  </span>
                )}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={cancelEdit}
                  disabled={model.saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={saveEdit}
                  /* Disabled on an untouched rule rather than hidden. A button
                     that appears once you type reads as the page not having
                     one, which is the state this replaces. */
                  disabled={!isAllowedToEdit || model.saving || !isDirty}
                >
                  {model.saving ? 'Saving...' : 'Save rule'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="ovr">
      {/* Above the list, not below it. A new rule is a `should be`, which is
          the group the engine runs first, so it lands at the top however the
          card is arranged; a button underneath meant clicking it moved the
          page out from under the pointer. A button per group would put the
          new rule where it was asked for, but the groups only exist once
          something is in them, so those buttons would come and go. */}
      {checkPermissions(
        PROFILES_PERMISSIONS.ARCHITECT,
        <Button
          size="sm"
          className="mb-3"
          onClick={createRule}
          disabled={!isAllowedToEdit || model.saving}
        >
          Create new rule
        </Button>
      )}

      {groups.map((group) => (
        <section className="ovr-group" key={group.should}>
          <div className="ovr-group-head">
            <h6 className="ovr-group-title">{group.should}</h6>
            <span className="ovr-group-when">{group.ordinal}</span>
          </div>
          {group.members.map(row)}
        </section>
      ))}

      {ungrouped.length > 0 && (
        <section className="ovr-group">
          <div className="ovr-group-head">
            <h6 className="ovr-group-title">Other</h6>
          </div>
          {ungrouped.map(row)}
        </section>
      )}
    </div>
  );
}

export default inject('store')(observer(OverrideRules));
